import type { Theme as MuiTheme, SxProps } from "@mui/material";

import { alpha, Box, GlobalStyles, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	CellClassParams,
	ColDef,
	ColumnState,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridOptions,
	GridReadyEvent,
	iconSetMaterial,
	MenuItemDef,
	Theme,
	themeBalham,
	ValueFormatterParams
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { debounce } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import "~/components/grid/agGridModules";

import checkCondition from "~/components/grid/conditionalFormatting/checkCondition";
import FormattingPanel from "~/components/grid/conditionalFormatting/FormattingPanel";
import GridEmptyState from "~/components/grid/GridEmptyState";
import GridLoadingOverlay from "~/components/grid/GridLoadingOverlay";
import GridLockOverlay from "~/components/grid/GridLockOverlay";
import { gridContainerSx, gridInnerSx } from "~/components/grid/gridLayoutStyles";
import GridRowCountStatusPanel from "~/components/grid/statusPanels/GridRowCountStatusPanel";
import useAgGridLocaleText from "~/components/grid/useAgGridLocaleText";
import useGridFilterFeedback from "~/components/grid/useGridFilterFeedback";
import useReactiveGridOptions from "~/components/grid/useReactiveGridOptions";
import { formatCurrencyCell } from "~/helpers/grid/coerceCellNumber";
import { routeExportMenuItems } from "~/helpers/grid/contextMenuDefaultItems";
import { DEFAULT_GRID_COMPARATOR } from "~/helpers/grid/defaultGridComparator";
import guardGetRowIdAgainstStubRows from "~/helpers/grid/guardGetRowIdAgainstStubRows";
import guardSetFilterFormatters from "~/helpers/grid/guardSetFilterFormatters";
import { isServerSideRowModel } from "~/helpers/grid/isServerSideRowModel";
import overriddenCellGlobalStyle, { OVERRIDDEN_CELL_CLASS } from "~/helpers/grid/overriddenCellStyling";
import SSRM_GRID_DEFAULTS, { SSRM_STATUS_BAR } from "~/helpers/grid/ssrmGridDefaults";
import { toolPanelsDefault } from "~/helpers/grid/toolPanels";
import AppThemeContext from "~/providers/AppThemeContext";

const LATE_READY_CELL_TINT_ALPHA = 0.15;

interface AgGridWithoutRibbonProps<TData> {
	classSuffix?: string;
	embedded?: boolean;
	externalFormattingTrigger?: React.MutableRefObject<(() => void) | null>;
	getRowId: (params: { data: TData }) => string;
	gridDataKey: string;
	gridOptions?: GridOptions;
	gridRef: React.RefObject<AgGridReact<TData>>;
	isProcessing?: boolean;
	isError?: boolean;
	onRetry?: () => void;
	/** Opt-in: when a global-search quick filter empties this grid, show a "No results" state with a clear-search CTA. */
	quickFilterEmptyState?: boolean;
	rowData?: TData[];
	suppressStatePersistence?: boolean;
	transformLoadedColumnState?: (state: ColumnState[], liveColIds: ReadonlySet<string>) => ColumnState[];
}

const AgGridWithoutRibbon = <TData,>({
	classSuffix,
	embedded = false,
	externalFormattingTrigger,
	getRowId,
	gridDataKey,
	gridOptions,
	gridRef,
	isError = false,
	isProcessing = false,
	onRetry,
	quickFilterEmptyState = false,
	rowData,
	suppressStatePersistence = false,
	transformLoadedColumnState,
	...nativeProps
}: AgGridWithoutRibbonProps<TData> & Partial<React.ComponentProps<typeof AgGridReact<TData>>>) => {
	const { themeSize } = useContext(AppThemeContext);
	const muiTheme = useTheme();
	const localeText = useAgGridLocaleText();
	const [formattingRules, setFormattingRules] = useState<GridFormattingRule[]>([]);
	const [formattingPanelOpen, setFormattingPanelOpen] = useState(false);
	const formattingRulesRef = useRef<GridFormattingRule[]>([]);
	const [gridStateRestored, setGridStateRestored] = useState(false);

	useEffect(() => {
		formattingRulesRef.current = formattingRules;
	}, [formattingRules]);

	// Expose formatting trigger to parent if provided
	useEffect(() => {
		if (externalFormattingTrigger) {
			externalFormattingTrigger.current = () => setFormattingPanelOpen(true);
		}
		return () => {
			if (externalFormattingTrigger) {
				externalFormattingTrigger.current = null;
			}
		};
	}, [externalFormattingTrigger]);

	const gridStateRestoredRef = useRef(false);
	const gridStateRestoreTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	useEffect(() => {
		gridStateRestoredRef.current = gridStateRestored;
	}, [gridStateRestored]);

	const saveGridState = useCallback(() => {
		if (suppressStatePersistence) return;
		if (gridRef.current && gridStateRestoredRef.current) {
			const columnState = gridRef.current.api.getColumnState();
			const filterModel = gridRef.current.api.getFilterModel();
			const selectedRows = gridRef.current.api.getSelectedNodes().map((node) => node.id);
			const focusedCell = gridRef.current.api.getFocusedCell();
			const focusedRowNode = focusedCell ? gridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex) : undefined;
			const focusedRowId = focusedRowNode ? focusedRowNode.id : null;
			const focusedColumn = focusedCell ? focusedCell.column.getColId() : null;
			const firstVisibleRowIndex = gridRef.current.api.getFirstDisplayedRowIndex();
			const firstVisibleColumn = gridRef.current.api.getAllDisplayedColumns()[0]?.getColId();
			const state = {
				columnState,
				filterModel,
				firstVisibleColumn,
				firstVisibleRowIndex,
				focusedColumn,
				focusedRowId,
				selectedRows
			};
			localStorage.setItem("gridstate-" + gridDataKey, JSON.stringify(state));
		}
	}, [gridRef, gridDataKey, suppressStatePersistence]);

	const loadGridState = useCallback(() => {
		if (suppressStatePersistence) return;
		const savedState = localStorage.getItem("gridstate-" + gridDataKey);
		if (savedState && gridRef.current) {
			const state = JSON.parse(savedState);
			const liveColIds = new Set((gridRef.current.api.getColumns() ?? []).map((column) => column.getColId()));
			gridRef.current.api.applyColumnState({
				applyOrder: true,
				state: transformLoadedColumnState
					? transformLoadedColumnState(state.columnState, liveColIds)
					: state.columnState
			});
			gridRef.current.api.setFilterModel(state.filterModel);
			state.selectedRows.forEach((id: string) => {
				const node = gridRef.current?.api.getRowNode(id);
				if (node) node.setSelected(true);
			});
			if (state.focusedRowId && state.focusedColumn) {
				const node = gridRef.current.api.getRowNode(state.focusedRowId);
				if (node && node.rowIndex !== null) {
					gridRef.current.api.setFocusedCell(node.rowIndex, state.focusedColumn);
					gridRef.current.api.ensureIndexVisible(node.rowIndex, "middle");
					gridRef.current.api.ensureColumnVisible(state.focusedColumn);
				} else {
					if (state.firstVisibleRowIndex !== undefined) {
						gridRef.current.api.ensureIndexVisible(state.firstVisibleRowIndex, "top");
					}
					if (state.firstVisibleColumn) {
						gridRef.current.api.ensureColumnVisible(state.firstVisibleColumn);
					}
				}
			} else {
				if (state.firstVisibleRowIndex !== undefined) {
					gridRef.current.api.ensureIndexVisible(state.firstVisibleRowIndex, "top");
				}
				if (state.firstVisibleColumn) {
					gridRef.current.api.ensureColumnVisible(state.firstVisibleColumn);
				}
			}
		}

		gridStateRestoreTimer.current = setTimeout(() => setGridStateRestored(true), 3000);
	}, [gridRef, gridDataKey, suppressStatePersistence, transformLoadedColumnState]);

	const debouncedSaveGridState = useMemo(() => debounce(saveGridState, 500), [saveGridState]);

	useEffect(() => {
		return () => {
			saveGridState();
		};
	}, [saveGridState]);

	// A restore timer left pending at unmount calls setState after the host environment is gone.
	useEffect(() => () => clearTimeout(gridStateRestoreTimer.current), []);

	const defaultGridOptions: GridOptions<TData> = useMemo(() => {
		return {
			columnDefs: [],
			columnTypes: {
				currency: {
					valueFormatter: (p: ValueFormatterParams) => formatCurrencyCell(p.value),
					width: 150
				}
			},
			defaultColDef: {
				cellClassRules: {
					"grid-cell-editable": (params) => !!params.colDef?.editable
				},
				cellStyle: (params: CellClassParams) => {
					const currentRules = formattingRulesRef.current;
					const rulesForColumn = currentRules.filter(
						(rule) => rule.conditions[0].column === params.colDef.field && rule.active && !rule.applyToRow
					);
					for (const rule of rulesForColumn) {
						const styling = checkCondition(params, rule);
						if (styling) return styling;
					}
				},
				filter: true,
				flex: 1,
				minWidth: 120,
				suppressHeaderMenuButton: true
			},
			getRowStyle: (params) => {
				const currentRules = formattingRulesRef.current;
				const rulesForRow = currentRules.filter((rule) => rule.applyToRow && rule.active);
				for (const rule of rulesForRow) {
					const styling = checkCondition(params, rule);
					if (styling) {
						return styling;
					}
				}
			},
			loadingOverlayComponent: GridLoadingOverlay,
			noRowsOverlayComponent: GridEmptyState,
			rowGroupPanelShow: "onlyWhenGrouping",
			sideBar: {
				toolPanels: toolPanelsDefault
			},
			statusBar: {
				statusPanels: [
					{ align: "left", statusPanel: GridRowCountStatusPanel },
					{ align: "center", statusPanel: "agAggregationComponent" }
				]
			},
			undoRedoCellEditing: true,
			undoRedoCellEditingLimit: 100
		};
	}, []);

	useEffect(() => {
		if (gridRef.current?.api) {
			gridRef.current.api.refreshCells({ force: true });
			gridRef.current.api.redrawRows();
		}
	}, [gridRef]);

	const serverSideRowModel = isServerSideRowModel(nativeProps.rowModelType, gridOptions);

	// No full-export context here, so a server-side grid drops AG Grid's partial (cached-rows-only) Export.
	const customGetContextMenuItems = nativeProps.getContextMenuItems ?? gridOptions?.getContextMenuItems;
	const mergedGetContextMenuItems = useMemo(() => {
		if (!serverSideRowModel) return customGetContextMenuItems;
		return (params: GetContextMenuItemsParams<TData>) =>
			routeExportMenuItems(
				customGetContextMenuItems
					? (customGetContextMenuItems(params as GetContextMenuItemsParams) as (DefaultMenuItem | MenuItemDef)[])
					: [...(params.defaultItems ?? [])],
				{ isServerSide: true }
			);
	}, [customGetContextMenuItems, serverSideRowModel]);

	const baseGridOptions = {
		...defaultGridOptions,
		...(serverSideRowModel ? SSRM_GRID_DEFAULTS : undefined),
		...(serverSideRowModel && !gridOptions?.statusBar ? { statusBar: SSRM_STATUS_BAR } : undefined),
		...gridOptions
	};
	const filterFeedback = useGridFilterFeedback();
	const guardedNativeColumnDefs = useMemo(
		() => guardSetFilterFormatters(nativeProps.columnDefs ?? []),
		[nativeProps.columnDefs]
	);
	const guardedColumnDefs = useMemo(
		() => guardSetFilterFormatters(baseGridOptions.columnDefs ?? []),
		[baseGridOptions.columnDefs]
	);
	const combinedGridOptions: GridOptions<TData> = {
		...baseGridOptions,
		defaultColDef: { comparator: DEFAULT_GRID_COMPARATOR, ...baseGridOptions.defaultColDef },
		columnDefs: guardedColumnDefs,
		...(mergedGetContextMenuItems ? { getContextMenuItems: mergedGetContextMenuItems } : {}),
		noRowsOverlayComponentParams: {
			...baseGridOptions.noRowsOverlayComponentParams,
			isError,
			onRetry,
			quickFilterEmptyState
		}
	};

	const applyLatestGridOptions = useReactiveGridOptions({
		gridDataKey,
		gridOptions,
		gridRef,
		wrapperResolvedOptions: {
			defaultColDef: combinedGridOptions.defaultColDef,
			getContextMenuItems: mergedGetContextMenuItems,
			noRowsOverlayComponentParams: combinedGridOptions.noRowsOverlayComponentParams
		}
	});

	const consumerOnGridReady = nativeProps.onGridReady ?? gridOptions?.onGridReady;
	const mergedOnGridReady = useCallback(
		(event: GridReadyEvent<TData>) => {
			filterFeedback.setApi(event.api);
			applyLatestGridOptions(event.api);
			consumerOnGridReady?.(event);
		},
		[applyLatestGridOptions, consumerOnGridReady, filterFeedback.setApi]
	);

	// AG Grid gives a direct prop precedence over the same gridOptions key, so the consumer's handler runs here.
	const mergedOnFilterChanged = useCallback(() => {
		saveGridState();
		(gridOptions?.onFilterChanged as (() => void) | undefined)?.();
	}, [gridOptions, saveGridState]);

	const getStubSafeRowId = useMemo(() => guardGetRowIdAgainstStubRows(getRowId), [getRowId]);

	const myTheme = themeBalham.withPart(iconSetMaterial).withParams({
		fontSize: Number(themeSize),
		headerRowBorder: true,
		wrapperBorder: false,
		...(embedded && {
			backgroundColor: "transparent",
			headerBackgroundColor: "transparent",
			headerHeight: 36,
			oddRowBackgroundColor: "transparent",
			rowHeight: 32
		})
	});

	const theme = useMemo<Theme | "legacy">(() => {
		return myTheme;
	}, [myTheme]);

	const containerSxArray = [
		gridContainerSx.normal,
		...(gridOptions?.domLayout === "autoHeight" ? [gridContainerSx.autoheight] : []),
		...(classSuffix && classSuffix in gridContainerSx
			? [gridContainerSx[classSuffix as keyof typeof gridContainerSx]]
			: []),
		{
			backgroundColor: (theme: MuiTheme) => (embedded ? "transparent" : theme.palette.background.paper),
			position: "relative"
		}
	] as SxProps<MuiTheme>;

	const innerSxArray = [
		gridInnerSx.normal,
		...(classSuffix && classSuffix in gridInnerSx ? [gridInnerSx[classSuffix as keyof typeof gridInnerSx]] : []),
		{ position: "relative" }
	] as SxProps<MuiTheme>;

	return (
		<Box component={embedded || classSuffix === "tab" ? "div" : Paper} sx={containerSxArray}>
			<GlobalStyles
				styles={{
					".ag-number-field-input": {
						textAlign: "right"
					},
					".grid-cell-editable": {
						backgroundColor: "#2244cc25"
					},
					".grid-cell-edited": {
						backgroundColor: "#44cc2225"
					},
					".grid-cell-late-ready": {
						backgroundColor: alpha(muiTheme.palette.warning.main, LATE_READY_CELL_TINT_ALPHA)
					},
					[`.${OVERRIDDEN_CELL_CLASS}`]: overriddenCellGlobalStyle(muiTheme)
				}}
			/>
			<Box sx={innerSxArray}>
				<AgGridReact
					{...nativeProps}
					{...(nativeProps.columnDefs ? { columnDefs: guardedNativeColumnDefs } : {})}
					{...(mergedGetContextMenuItems ? { getContextMenuItems: mergedGetContextMenuItems } : {})}
					cellSelection={nativeProps.cellSelection ?? true}
					enterNavigatesVerticallyAfterEdit={nativeProps.enterNavigatesVerticallyAfterEdit ?? true}
					getRowId={getStubSafeRowId}
					gridOptions={combinedGridOptions}
					postProcessPopup={(params) => {
						filterFeedback.postProcessPopup(params);
						(nativeProps.postProcessPopup ?? gridOptions?.postProcessPopup)?.(params);
					}}
					localeText={nativeProps.localeText ?? localeText}
					onBodyScroll={debouncedSaveGridState}
					onCellFocused={saveGridState}
					onColumnMoved={saveGridState}
					onColumnResized={saveGridState}
					onColumnValueChanged={saveGridState}
					onColumnVisible={saveGridState}
					onFilterChanged={mergedOnFilterChanged}
					onFirstDataRendered={loadGridState}
					onGridReady={mergedOnGridReady}
					onSelectionChanged={(event) => {
						saveGridState();
						if (nativeProps.onSelectionChanged) {
							nativeProps.onSelectionChanged(event);
						}
					}}
					onSortChanged={(event) => {
						saveGridState();
						if (nativeProps.onSortChanged) {
							nativeProps.onSortChanged(event);
						}
					}}
					ref={gridRef}
					{...(serverSideRowModel ? {} : { rowData })}
					rowSelection={
						nativeProps.rowSelection ??
						(embedded
							? { checkboxes: false, copySelectedRows: true, mode: "multiRow", selectAll: "filtered" }
							: { copySelectedRows: true, mode: "multiRow", selectAll: "filtered" })
					}
					theme={theme}
				/>
				{filterFeedback.errorPortal}
				<GridLockOverlay isProcessing={isProcessing} />
			</Box>
			{formattingPanelOpen && (
				<FormattingPanel
					columns={gridOptions?.columnDefs as ColDef[]}
					formattingPanelOpen={formattingPanelOpen}
					gridDataKey={gridDataKey}
					rules={formattingRules}
					setFormattingPanelOpen={setFormattingPanelOpen}
					setRules={setFormattingRules}
				/>
			)}
		</Box>
	);
};

export default AgGridWithoutRibbon;
